/**
 * PatientAutocomplete — inline suggestion dropdown for the New-Patient form.
 * As the user types phone/name/email, we query /patients/search-suggest
 * (debounced 300 ms) and show matching patients so they can pick an
 * existing one instead of creating a duplicate.
 */
import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Search, User as UserIcon, Phone as PhoneIcon, Mail, ArrowUpRight, Clock } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function PatientAutocomplete({ value, onPickExisting }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const q = (value || "").trim();
    if (q.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/patients/search-suggest`, { params: { q, limit: 5 } });
        setSuggestions(data.suggestions || []);
        setOpen((data.suggestions || []).length > 0);
      } catch {
        // silent — autocomplete is a hint, not required for the flow
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [value]);

  if (!open) return null;

  return (
    <div className="mt-1 rounded-xl border border-[#E8EAE9] bg-white shadow-lg overflow-hidden"
         data-testid="patient-autocomplete">
      <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-ink-soft border-b border-[#EFEADC] flex items-center gap-2">
        <Search className="w-3 h-3" /> Existing patients matching this
        {loading && <Clock className="w-3 h-3 animate-spin ml-auto" />}
      </div>
      <div className="max-h-56 overflow-y-auto divide-y divide-[#EFEADC]">
        {suggestions.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => { onPickExisting?.(s); setOpen(false); }}
            data-testid={`autocomplete-item-${s.id}`}
            className="w-full text-left px-3 py-2.5 hover:bg-[#FBFAF6] flex items-center gap-3 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-[#F0EBDD] text-ink grid place-items-center shrink-0">
              <UserIcon className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm text-ink truncate">{s.name}</div>
              <div className="text-xs text-ink-mid truncate flex items-center gap-2">
                {s.phone && <span className="flex items-center gap-1"><PhoneIcon className="w-3 h-3" />{s.phone}</span>}
                {s.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{s.email}</span>}
              </div>
              <div className="text-[10px] text-ink-soft mt-0.5">
                {s.last_visit ? `Last visit ${fmtDate(s.last_visit)}` : `Added ${fmtDate(s.created_at)}`}
              </div>
            </div>
            <Link
              to={`/app/patients/${s.id}`}
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 inline-flex items-center gap-1 text-[11px] text-ink hover:underline"
            >
              Open <ArrowUpRight className="w-3 h-3" />
            </Link>
          </button>
        ))}
      </div>
    </div>
  );
}
